import { BlueprintInstanceType } from '../shared.enums';
import { getStateCfn } from './functions';
import { Notification } from './notification.class';
import { NotificationType } from './enums';

import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';
import { NotificationCountData, NotificationParams, NotificationsDataIn, NotificationsDataOut } from './types';

export class Notifications extends KitchenDesignBase<NotificationsDataIn> {
	readonly children: Array<Notification>;
	/**
	 * The constructor of the 'Notification' class
	 * @param options provides context, also used to hydrate the instance
	*/
	constructor (options: KitchenDesignOptions<NotificationsDataIn> = {}) {
		super(BlueprintInstanceType.Notifications, options);

		const { dataIn: data = null } = options;

		this.children = [];
		if (data && data.children?.length > 0) {
			data.children?.forEach((n) => this.children.push(new Notification({ dataIn: n, parent: this })));
		}

		this.setInitialized();
	}

	get state (): NotificationType { return getStateCfn(this) }

	/**
	 *
	 * @param id id of notification item to find
	 * @returns Notification
	*/
	public findById (id: string): Notification {
		return this.children.find((pi) => pi.id === id);
	}

	public filterByRefId (refId: string): Notification[] {
		return this.children.filter((notification) => notification.refId === refId);
	}

	public getNotifications (notificationCode: number | null = 255): Notification[] {
		return this.children.filter((notification) => notification.type & notificationCode);
	}

	public getCounts (): NotificationCountData {
		return {
			total: this.children.length,
			info: this.children.filter((notification) => NotificationType.Info === notification.type).length,
			warn: this.children.filter((notification) => NotificationType.Warn === notification.type).length,
			error: this.children.filter((notification) => NotificationType.Error === notification.type).length,
			critical: this.children.filter((notification) => NotificationType.Critical === notification.type).length
		}
	}

	public add (dataIn: Partial<NotificationParams>): void {
		const newNotification = new Notification({
			dataIn: {
				refId: dataIn.refId,
				itemRefId: dataIn.itemRefId,
				validationCode: dataIn.validationCode,
				description: dataIn.description,
				type: dataIn.type
			}
		})
		this.children.push(newNotification);
	}

	public purgeReferences (validationCode: string, refId: string | null = null): void {
		for (let i = this.children.length - 1; i >= 0; i--) {
			if ((this.children[i].refId === refId || refId === null) && this.children[i].validationCode === validationCode) {
				this.children.splice(i, 1);
			}
		}
	}

	// serialize
	public serialize (): NotificationsDataOut {
		return {
			children: this.children.map((notification) => notification.serialize()),
			_metadata: {
				state: this.state,
				counts: this.getCounts()
			}
		};
	}
}
